import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Loader2, CheckCircle, AlertTriangle } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | success | failed | timeout
  const [tx, setTx] = useState(null);
  const { clear } = useCart();
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!session_id) {
      setStatus("failed");
      return;
    }
    const poll = async () => {
      if (attemptsRef.current >= 8) {
        setStatus("timeout");
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${session_id}`);
        setTx(data);
        if (data.payment_status === "paid") {
          setStatus("success");
          clear();
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
      } catch {
        // continue
      }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session_id]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-success-page">
      {status === "checking" && (
        <>
          <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#00E5FF]" />
          <h1 className="font-heading uppercase text-4xl mt-6">Confirming payment</h1>
          <p className="font-sub text-white/50 mt-3">Hold tight — checking your transaction.</p>
        </>
      )}
      {status === "success" && (
        <>
          <CheckCircle className="w-12 h-12 mx-auto text-[#00FF66]" />
          <h1 className="font-heading uppercase text-5xl mt-6" data-testid="success-heading">
            Order Forged
          </h1>
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#00E5FF] mt-3">
            ${(tx?.amount_total / 100 || 0).toFixed(2)} {tx?.currency?.toUpperCase()}
          </div>
          <p className="font-sub text-white/60 mt-6">Welcome to the forge. Your order is confirmed and a receipt has been logged.</p>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <Link to="/dashboard" className="btn-forged-red">View Orders</Link>
            <Link to="/shop" className="btn-forged-ghost">Keep Shopping</Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "timeout") && (
        <>
          <AlertTriangle className="w-12 h-12 mx-auto text-[#FF3333]" />
          <h1 className="font-heading uppercase text-4xl mt-6">Payment not confirmed</h1>
          <p className="font-sub text-white/60 mt-3">
            {status === "timeout" ? "Confirmation timed out. Check your email or contact support." : "Payment failed or expired."}
          </p>
          <Link to="/cart" className="btn-forged-blue mt-8 inline-flex">Back to Cart</Link>
        </>
      )}
    </div>
  );
}
